import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { timeSlots } from '../mock';
import { toast } from '../hooks/use-toast';
import { CalendarIcon, Clock, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { utmPayload } from '../lib/tracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingModal = ({ open, onOpenChange }) => {
  const [date, setDate] = useState();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    timeSlot: '',
    message: '',
    referralCode: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !formData.timeSlot) {
      toast({
        title: 'Missing Information',
        description: 'Please select a date and time for your appointment.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const bookingData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        date: format(date, 'PPP'),
        timeSlot: formData.timeSlot,
        message: formData.message || '',
        ...utmPayload(),
      };
      
      const response = await axios.post(`${API}/bookings`, bookingData);
      
      if (response.data.success) {
        toast({
          title: 'Booking Confirmed!',
          description: `We'll call you on ${format(date, 'PPP')} at ${formData.timeSlot}. Check your email for confirmation.`,
        });
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          timeSlot: '',
          message: '',
          referralCode: ''
        });
        setDate(undefined);
        onOpenChange(false);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast({
        title: 'Booking Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-gray-900">Book Your Consultation Call</DialogTitle>
          <DialogDescription>
            Schedule a call with our wine experts. We'll help you choose the perfect wine kit and answer all your questions.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-gray-900">Your Information</h3>
              
              <div className="space-y-2">
                <Label htmlFor="firstName" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  First Name *
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  required
                  placeholder="John"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  required
                  placeholder="Doe"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  placeholder="john@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Message (Optional)
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Tell us about your wine preferences or any questions you have..."
                  rows={4}
                />
              </div>
            </div>

            {/* Date & Time Selection */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-gray-900">Select Date & Time</h3>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  Choose a Date *
                </Label>
                <div className="border rounded-md p-3">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(date) => date < new Date() || date.getDay() === 0}
                    className="rounded-md"
                  />
                </div>
                {date && (
                  <p className="text-sm text-gray-600 mt-2">
                    Selected: {format(date, 'PPP')}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="timeSlot" className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Preferred Time *
                </Label>
                <Select value={formData.timeSlot} onValueChange={(value) => handleChange('timeSlot', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="referralCode" className="text-gray-700">
                  Referral Code (Optional)
                </Label>
                <Input
                  id="referralCode"
                  type="text"
                  placeholder="Enter referral code if you have one"
                  value={formData.referralCode}
                  onChange={(e) => handleChange('referralCode', e.target.value)}
                  className="border-gray-300"
                />
              </div>

              <div className="bg-[#FDB913]/10 border border-[#FDB913]/30 rounded-lg p-4 mt-4">
                <h4 className="font-semibold text-gray-900 mb-2">What to Expect:</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• 15-30 minute consultation call</li>
                  <li>• Wine selection guidance</li>
                  <li>• Process overview and timeline</li>
                  <li>• Pricing and package options</li>
                  <li>• Answer all your questions</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit"
              data-track="book_call:booking_modal_submit"
              disabled={isSubmitting}
              className="bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900 font-semibold"
            >
              {isSubmitting ? 'Booking...' : 'Confirm Booking'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;